import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Phone, Mail, X } from "lucide-react";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { STATUSES, STRATEGIES, CAPACITY, AML, badge, ukDate } from "./crmShared";

export default function CrmInvestors() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "", strategy: "", buying_capacity: "", aml_status: "" });

  const due = searchParams.get("due") || "";
  const statusParam = searchParams.get("status") || "";

  useEffect(() => {
    if (statusParam) setFilters((f) => ({ ...f, status: statusParam }));
  }, [statusParam]);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (due) params.due = due;
    api.get("/crm/investors", { params }).then((r) => setList(r.data));
  }, [filters, due]);

  const clearBanner = () => {
    setFilters((f) => ({ ...f, status: "" }));
    setSearchParams({});
  };

  const bannerLabel = due ? (due === "today" ? "Follow-ups due today" : "Overdue follow-ups")
    : statusParam ? `Status: ${statusParam}` : "";

  return (
    <CrmShell title="Investor Leads">
      {bannerLabel && (
        <div className="flex items-center justify-between bg-brass/15 border border-brass/30 rounded-lg px-4 py-2 mb-4" data-testid="inv-filter-banner">
          <span className="text-sm text-brass">Filtered by <strong>{bannerLabel}</strong> · {list.length} result{list.length === 1 ? "" : "s"}</span>
          <button onClick={clearBanner} data-testid="inv-filter-clear" className="flex items-center gap-1 text-xs text-emerald-100/70 hover:text-white"><X className="w-3.5 h-3.5" /> Clear</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-2.5 text-emerald-100/40" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search name, email, phone, company" data-testid="inv-search" className="w-full bg-[#0F2E23] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-emerald-100/40" /></div>
        <select value={filters.status} onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); if (due) setSearchParams({}); }} data-testid="inv-filter-status" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <select value={filters.strategy} onChange={(e) => setFilters((f) => ({ ...f, strategy: e.target.value }))} data-testid="inv-filter-strategy" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"><option value="">All strategies</option>{STRATEGIES.map((s) => <option key={s}>{s}</option>)}</select>
        <select value={filters.buying_capacity} onChange={(e) => setFilters((f) => ({ ...f, buying_capacity: e.target.value }))} data-testid="inv-filter-capacity" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"><option value="">Any capacity</option>{CAPACITY.map((s) => <option key={s}>{s}</option>)}</select>
        <select value={filters.aml_status} onChange={(e) => setFilters((f) => ({ ...f, aml_status: e.target.value }))} data-testid="inv-filter-aml" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"><option value="">Any AML</option>{AML.map((s) => <option key={s}>{s}</option>)}</select>
        <button onClick={() => navigate("/crm/investors/new")} data-testid="inv-new" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-4 py-2 text-sm"><Plus className="w-4 h-4" /> Add Investor Lead</button>
      </div>
      <div className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-x-auto" data-testid="inv-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-emerald-100/50 border-b border-white/10">
            <th className="py-2.5 px-3">Name</th>
            <th className="py-2.5 px-3 hidden sm:table-cell">Contact</th>
            <th className="py-2.5 px-3 hidden md:table-cell">Buys</th>
            <th className="py-2.5 px-3 hidden lg:table-cell">Source</th>
            <th className="py-2.5 px-3">Status</th>
            <th className="py-2.5 px-3 hidden lg:table-cell">Follow-up</th>
          </tr></thead>
          <tbody>{list.map((i) => (
            <tr key={i.id} onClick={() => navigate(`/crm/investors/${i.id}`)} data-testid={`inv-row-${i.id}`} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
              <td className="py-3 px-3"><div className="text-white font-medium">{i.first_name} {i.last_name}</div><div className="text-xs text-emerald-100/50">{(i.criteria?.strategies || []).join(", ") || i.entity_type || "—"}</div></td>
              <td className="py-3 px-3 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-col gap-0.5">
                  {i.telephone && <a href={`tel:${i.telephone}`} data-testid={`inv-tel-${i.id}`} className="flex items-center gap-1 text-emerald-100/70 hover:text-brass text-xs"><Phone className="w-3 h-3" />{i.telephone}</a>}
                  {i.email && <a href={`mailto:${i.email}`} data-testid={`inv-mail-${i.id}`} className="flex items-center gap-1 text-emerald-100/70 hover:text-brass text-xs"><Mail className="w-3 h-3" />{i.email}</a>}
                </div>
              </td>
              <td className="py-3 px-3 hidden md:table-cell text-emerald-100/70 text-xs">{i.criteria?.locations || "—"}</td>
              <td className="py-3 px-3 hidden lg:table-cell text-emerald-100/60 text-xs">{i.source || "—"}</td>
              <td className="py-3 px-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${badge(i.pipeline_status)}`}>{i.pipeline_status}</span></td>
              <td className="py-3 px-3 hidden lg:table-cell text-emerald-100/60 text-xs">{ukDate(i.next_follow_up)}</td>
            </tr>))}</tbody>
        </table>
        {list.length === 0 && <p className="text-emerald-100/50 text-sm py-8 text-center">No investor leads match.</p>}
      </div>
    </CrmShell>
  );
}
